/**
 * VS Insights Data Contract — v1.0
 * =================================
 * Contract-only definition. No implementation logic.
 *
 * This file defines the stable interface between the UI layer and the
 * data layer (insightsService.ts). All components consume these types;
 * no component queries Supabase directly.
 *
 * Source views (deployed & verified 2026-05-10):
 *   lender_insights_overview_v   — daily + monthly KPI snapshots
 *   lender_acquisition_v         — monthly member growth metrics
 *   lender_loan_performance_v    — empty until LOS connected
 *   lender_underserved_v         — low-band financial health metrics
 *   lender_financial_health_v    — score band aggregates + trend
 *
 * Canonical metric definitions: vs_metric_definitions (10 seeded keys)
 * RLS: 42 policies across 14 tables — tenant isolation enforced.
 */

// ============================================================================
// 1. CORE ENUMS
// ============================================================================

/** Connection status of a metric's data source */
export type DataSourceStatus = 'live' | 'partial' | 'not_connected';

/** What integration is required for NOT_CONNECTED metrics */
export type RequiredIntegration =
  | 'los'           // Loan Origination System
  | 'core_banking'  // Core Banking / GL
  | 'crm'           // CRM / Member Management
  | 'survey'        // Survey / NPS platform
  | 'algorithm'     // Internal algorithm extension
  | 'none';         // No external dependency

/** Directionality — drives sentiment color logic */
export type Directionality = 'higher_is_better' | 'lower_is_better' | 'neutral';

/** Data sufficiency tier — drives UI treatment */
export type DataSufficiency = 'empty' | 'insufficient' | 'directional' | 'standard';

// ============================================================================
// 2. METRIC VALUE — The atomic unit
// ============================================================================

/**
 * Every metric in the system is wrapped in this envelope.
 * Components render based on `status` — never on value truthiness.
 */
export interface MetricValue {
  /** The metric value. null when status != 'live' */
  value: number | null;

  /** Period-over-period change (e.g., +5.2). null if insufficient history */
  trend: number | null;

  /** Data source connection status */
  status: DataSourceStatus;

  /** Human-readable source label (e.g., "lender_financial_health_v") */
  source: string;

  /** Number of members backing this metric */
  sample_size: number;

  /** Data sufficiency classification derived from sample_size */
  sufficiency: DataSufficiency;

  /** Directionality for sentiment color rendering */
  directionality: Directionality;
}

// ============================================================================
// 3. NOT CONNECTED METADATA
// ============================================================================

/** Displayed when status === 'not_connected' */
export interface NotConnectedInfo {
  /** Short label: "Requires LOS Integration" */
  label: string;

  /** Extended description for tooltip/detail */
  description: string;

  /** Which system must be connected */
  required_system: RequiredIntegration;
}

// ============================================================================
// 4. CHART DATA SHAPES
// ============================================================================

/** Single point in a time-series chart */
export interface ChartDataPoint {
  /** ISO date or YYYY-MM label */
  period: string;
  value: number;
}

/** Time-series dataset for line/bar charts */
export interface ChartSeries {
  label: string;
  data: ChartDataPoint[];
  status: DataSourceStatus;
}

/** Score band distribution bucket */
export interface ScoreBandBucket {
  band: string;              // '0-19', '20-39', '40-59', '60-79', '80-100'
  member_count: number;
  avg_score: number;
  avg_dti: number | null;
  avg_reserves: number | null;       // months
  avg_cash_flow_stability: number | null;
}

// ============================================================================
// 5. SECTION SHAPES — one per tab
// ============================================================================

/** Overview tab metrics */
export interface OverviewSection {
  total_members: MetricValue;
  avg_verascore: MetricValue;
  revenue_per_member: MetricValue;    // NOT_CONNECTED — core_banking
  member_satisfaction: MetricValue;   // NOT_CONNECTED — survey
  /** Portfolio Health Index — composite 0–100 anchor KPI */
  portfolio_health_index: MetricValue; // LIVE — computed from fh factors
  /** Member count growth — PARTIAL (consent dates only, no CRM onboard date) */
  member_growth_count: ChartSeries;   // PARTIAL — lender_acquisition_v
  /** Revenue growth — NOT_CONNECTED (requires Core Banking) */
  revenue_growth: ChartSeries;        // NOT_CONNECTED — core_banking
  loan_funnel: MetricValue;           // NOT_CONNECTED — los
  loan_portfolio: MetricValue;        // NOT_CONNECTED — los
  date_range: {
    start: string;
    end: string;
    label: string;
  };
}

/** Member Acquisition tab */
export interface AcquisitionSection {
  new_members: MetricValue;           // PARTIAL — consent-based, not CRM
  total_member_base: MetricValue;     // LIVE
  revenue_per_member: MetricValue;    // NOT_CONNECTED — core_banking
  avg_services: MetricValue;          // NOT_CONNECTED — core_banking
  monthly_acquisition_chart: ChartSeries;  // PARTIAL
  revenue_trend_chart: ChartSeries;   // NOT_CONNECTED — core_banking
  services_chart: ChartSeries;        // NOT_CONNECTED — core_banking
  tenure_chart: ChartSeries;          // NOT_CONNECTED — crm
}

/** Loan Performance tab — entirely NOT_CONNECTED until LOS */
export interface LoanPerformanceSection {
  conversion_rate: MetricValue;
  applications: MetricValue;
  avg_decision_time: MetricValue;
  default_rate: MetricValue;
  /** All metrics in this section share the same not_connected reason */
  not_connected_info: NotConnectedInfo;
}

/** Underserved Market tab */
export interface UnderservedSection {
  /**
   * Credit Invisible count — NOT_CONNECTED.
   * vs_member_segment_map exists but does not reliably populate nonzero
   * credit_invisible segments without LOS loan history context.
   */
  credit_invisible_count: MetricValue;    // NOT_CONNECTED — los (needs loan context)
  first_time_borrowers: MetricValue;      // NOT_CONNECTED — los
  avg_loan_gap: MetricValue;              // NOT_CONNECTED — los
  capacity_utilization: MetricValue;      // NOT_CONNECTED — los
  /** Score band distribution for underserved (low-band) members — LIVE */
  underserved_bands: ScoreBandBucket[];   // LIVE — lender_underserved_v
}

/** Score band migration data point (one per band per period) */
export interface BandMigrationPoint {
  period: string;  // ISO date or YYYY-MM
  band: string;    // '0-19', '20-39', etc.
  count: number;
}

/** Financial Health tab — PRIORITY TAB (most live data) */
export interface FinancialHealthSection {
  avg_verascore: MetricValue;             // LIVE — lender_financial_health_v
  score_consistency: MetricValue;         // NOT_CONNECTED — algorithm
  member_satisfaction: MetricValue;       // NOT_CONNECTED — survey
  stress_reduction: MetricValue;          // NOT_CONNECTED — survey
  /** P2B: Members reporting high financial stress (score > 70) — LIVE when survey data exists */
  high_financial_stress_count: MetricValue;
  /** P4C: Members with VeraScore decline ≥15 points in 90 days — LIVE from CoreMath */
  declining_score_90d_count: MetricValue;
  /** P4C-2A: Members with high debt-to-income ratio (>40%) — LIVE from CoreMath */
  dti_high_count: MetricValue;
  /** P4C-2B: Members with < 1 month liquid reserves — LIVE from CoreMath */
  liquidity_months_critical_count: MetricValue;
  score_distribution: ScoreBandBucket[];  // LIVE — lender_financial_health_v
  score_trend_chart: ChartSeries;         // LIVE — monthly_avg_score
  /** Score band migration over time — LIVE */
  score_band_migration: BandMigrationPoint[];
  /** Radar chart factor breakdown — only render axes with status === 'live' */
  factor_breakdown: {
    dti: MetricValue;                     // LIVE — lender_financial_health_v avg_dti
    liquid_reserves: MetricValue;         // LIVE — lender_financial_health_v avg_reserves
    cash_flow_stability: MetricValue;     // LIVE — lender_financial_health_v avg_cash_flow
    savings: MetricValue;                 // NOT_CONNECTED — algorithm (not yet computed)
    debt: MetricValue;                    // NOT_CONNECTED — algorithm (not yet computed)
    payment_history: MetricValue;         // NOT_CONNECTED — algorithm (not yet computed)
  };
  /** Known issue: DTI segment thresholds may be inverted */
  dti_segment_warning: boolean;
}

// ============================================================================
// 6. UNIFIED RESPONSE — Top-level contract
// ============================================================================

/**
 * The single response shape returned by useInsightsData().
 * Every component in the Insights dashboard consumes a slice of this.
 */
export interface InsightsResponse {
  overview: OverviewSection;
  acquisition: AcquisitionSection;
  loan_performance: LoanPerformanceSection;
  underserved: UnderservedSection;
  financial_health: FinancialHealthSection;

  /** P3+P4C: Behavioral aggregate counts for the rule engine (no PII) */
  behavioral?: {
    high_financial_stress_count: number;
    dti_high_count: number;
    /** P4C: Members with VeraScore decline ≥ 15 points in 90 days */
    declining_score_90d_count: number;
    /** P4C-2B: Members with < 1 month liquid reserves */
    liquidity_months_critical_count: number;
  };

  /** Global metadata */
  meta: InsightsMeta;
}

/** Global metadata attached to every response */
export interface InsightsMeta {
  /** Tenant ID (from auth context) */
  tenant_id: string;

  /** When this data was fetched */
  fetched_at: string;

  /** Freshness of underlying batch data (hours since last vs_daily_metrics_batch) */
  freshness_hours: number;

  /** Total connected members (for global sufficiency check) */
  total_sample_size: number;

  /** Global sufficiency level */
  sufficiency: DataSufficiency;
}

// ============================================================================
// 7. LOADING / ERROR WRAPPER — Hook return type
// ============================================================================

/** Return type of useInsightsData() hook */
export interface UseInsightsDataResult {
  data: InsightsResponse | null;
  loading: boolean;
  error: Error | null;
  /** Re-fetch all sections */
  refetch: () => Promise<void>;
}

// ============================================================================
// 8. DATA SUFFICIENCY RULES (pure function contract)
// ============================================================================

/**
 * Data sufficiency classification rules.
 * UI components use this to decide rendering treatment.
 *
 * 0 members     → 'empty'        → Empty state, no chart
 * 1–4 members   → 'insufficient' → Warning badge, muted chart
 * 5–29 members  → 'directional'  → Amber badge, "Directional only"
 * 30+ members   → 'standard'     → Green badge, full rendering
 */
export const SUFFICIENCY_THRESHOLDS = {
  EMPTY: 0,
  INSUFFICIENT_MAX: 4,
  DIRECTIONAL_MAX: 29,
  STANDARD_MIN: 30,
} as const;

// ============================================================================
// 9. NOT_CONNECTED REGISTRY — all reasons in one place
// ============================================================================

/**
 * Canonical not-connected reasons. Components reference by key.
 * Replaces all "Coming Soon" text with specific integration requirements.
 */
export const NOT_CONNECTED_REGISTRY: Record<RequiredIntegration, NotConnectedInfo> = {
  los: {
    label: 'Requires LOS Integration',
    description: 'This metric requires a connection to your Loan Origination System. Contact your VeraScore account manager to enable.',
    required_system: 'los',
  },
  core_banking: {
    label: 'Requires Core Banking Integration',
    description: 'This metric requires a connection to your Core Banking system for revenue and product data.',
    required_system: 'core_banking',
  },
  crm: {
    label: 'Requires CRM Integration',
    description: 'This metric requires member demographic and tenure data from your CRM system.',
    required_system: 'crm',
  },
  survey: {
    label: 'Requires Survey Data',
    description: 'This metric requires member satisfaction survey data. Enable VeraScore Survey module to populate.',
    required_system: 'survey',
  },
  algorithm: {
    label: 'Requires Algorithm Extension',
    description: 'This metric requires additional scoring algorithm capabilities currently under development.',
    required_system: 'algorithm',
  },
  none: {
    label: 'Live',
    description: 'This metric is sourced from live VeraScore data.',
    required_system: 'none',
  },
} as const;

// ============================================================================
// 10. METRIC CATALOG — All 49 metrics mapped
// ============================================================================

/**
 * Complete catalog of all UI metrics with their contract metadata.
 * This is the single source of truth for the Step 2 mapping.
 * 49 metrics: 9 LIVE, 2 PARTIAL, 38 NOT_CONNECTED.
 *
 * metric_key format: {section}.{metric_name}
 */
export interface MetricCatalogEntry {
  metric_key: string;
  section: 'overview' | 'acquisition' | 'loan_performance' | 'underserved' | 'financial_health';
  display_name: string;
  status: DataSourceStatus;
  source_view: string | null;
  required_integration: RequiredIntegration;
  directionality: Directionality;
  db_metric_key: string | null;        // maps to vs_metric_definitions.metric_key
  ui_component: string;                // which .tsx file renders this
}

/**
 * Full 49-metric catalog. Contract only — not used at runtime.
 * Serves as documentation and validation reference.
 */
export const METRIC_CATALOG: MetricCatalogEntry[] = [
  // ── Overview Tab (9 metrics) ──────────────────────────────────────────────
  { metric_key: 'overview.total_members',      section: 'overview', display_name: 'Total Members',        status: 'live',          source_view: 'lender_insights_overview_v', required_integration: 'none',         directionality: 'higher_is_better', db_metric_key: 'total_members',    ui_component: 'DashboardOverview.tsx' },
  { metric_key: 'overview.revenue_per_member', section: 'overview', display_name: 'Revenue per Member',   status: 'not_connected', source_view: null,                         required_integration: 'core_banking', directionality: 'higher_is_better', db_metric_key: null,               ui_component: 'DashboardOverview.tsx' },
  { metric_key: 'overview.member_satisfaction',section: 'overview', display_name: 'Member Satisfaction',   status: 'not_connected', source_view: null,                         required_integration: 'survey',       directionality: 'higher_is_better', db_metric_key: null,               ui_component: 'DashboardOverview.tsx' },
  { metric_key: 'overview.member_growth_count', section: 'overview', display_name: 'Member Growth (Count)',   status: 'partial',       source_view: 'lender_acquisition_v',       required_integration: 'none',         directionality: 'higher_is_better', db_metric_key: 'new_members',      ui_component: 'MemberGrowthChart.tsx' },
  { metric_key: 'overview.revenue_growth',      section: 'overview', display_name: 'Revenue Growth',          status: 'not_connected', source_view: null,                         required_integration: 'core_banking', directionality: 'higher_is_better', db_metric_key: null,               ui_component: 'MemberGrowthChart.tsx' },
  { metric_key: 'overview.loan_funnel',        section: 'overview', display_name: 'Loan Application Funnel', status: 'not_connected', source_view: null,                       required_integration: 'los',          directionality: 'higher_is_better', db_metric_key: null,               ui_component: 'LoanFunnelChart.tsx' },
  { metric_key: 'overview.loan_portfolio',     section: 'overview', display_name: 'Loan Portfolio Dist.',   status: 'not_connected', source_view: null,                        required_integration: 'los',          directionality: 'neutral',          db_metric_key: null,               ui_component: 'LoanPortfolioChart.tsx' },
  { metric_key: 'overview.conversion_rate',    section: 'overview', display_name: 'Conversion Rate',       status: 'not_connected', source_view: null,                         required_integration: 'los',          directionality: 'higher_is_better', db_metric_key: null,               ui_component: 'LoanPortfolioChart.tsx' },
  { metric_key: 'overview.date_range',         section: 'overview', display_name: 'Reporting Period',      status: 'live',          source_view: 'lender_insights_overview_v', required_integration: 'none',         directionality: 'neutral',          db_metric_key: null,               ui_component: 'InsightsDashboard.tsx' },

  // ── Member Acquisition Tab (9 metrics) ────────────────────────────────────
  { metric_key: 'acquisition.new_members',       section: 'acquisition', display_name: 'New Members (Monthly)',   status: 'partial',       source_view: 'lender_acquisition_v',       required_integration: 'none',         directionality: 'higher_is_better', db_metric_key: 'new_members',          ui_component: 'MemberAcquisition.tsx' },
  { metric_key: 'acquisition.total_member_base', section: 'acquisition', display_name: 'Total Member Base',      status: 'live',          source_view: 'lender_insights_overview_v', required_integration: 'none',         directionality: 'higher_is_better', db_metric_key: 'total_members',        ui_component: 'MemberAcquisition.tsx' },
  { metric_key: 'acquisition.revenue_per_member',section: 'acquisition', display_name: 'Revenue per Member',     status: 'not_connected', source_view: null,                         required_integration: 'core_banking', directionality: 'higher_is_better', db_metric_key: null,                   ui_component: 'MemberAcquisition.tsx' },
  { metric_key: 'acquisition.avg_services',      section: 'acquisition', display_name: 'Avg Services per Member',status: 'not_connected', source_view: null,                         required_integration: 'core_banking', directionality: 'higher_is_better', db_metric_key: null,                   ui_component: 'MemberAcquisition.tsx' },
  { metric_key: 'acquisition.monthly_chart',     section: 'acquisition', display_name: 'Monthly Acquisition',    status: 'partial',       source_view: 'lender_acquisition_v',       required_integration: 'none',         directionality: 'higher_is_better', db_metric_key: 'new_members',          ui_component: 'MonthlyMemberAcquisitionChart.tsx' },
  { metric_key: 'acquisition.revenue_trend',     section: 'acquisition', display_name: 'Revenue per Member Trend',status: 'not_connected',source_view: null,                         required_integration: 'core_banking', directionality: 'higher_is_better', db_metric_key: null,                   ui_component: 'RevenuePerMemberTrendChart.tsx' },
  { metric_key: 'acquisition.services_chart',    section: 'acquisition', display_name: 'Avg Services Trend',     status: 'not_connected', source_view: null,                         required_integration: 'core_banking', directionality: 'higher_is_better', db_metric_key: null,                   ui_component: 'AvgServicesPerMemberChart.tsx' },
  { metric_key: 'acquisition.tenure_chart',      section: 'acquisition', display_name: 'Members by Tenure',      status: 'not_connected', source_view: null,                         required_integration: 'crm',          directionality: 'neutral',          db_metric_key: null,                   ui_component: 'MemberSegmentsByTenureChart.tsx' },
  { metric_key: 'acquisition.progress_bars',     section: 'acquisition', display_name: 'Progress Towards Goals', status: 'not_connected', source_view: null,                         required_integration: 'core_banking', directionality: 'neutral',          db_metric_key: null,                   ui_component: 'MemberAcquisition.tsx' },

  // ── Loan Performance Tab (9 metrics) — ALL NOT_CONNECTED ──────────────────
  { metric_key: 'loan_performance.conversion_rate',   section: 'loan_performance', display_name: 'Conversion Rate',          status: 'not_connected', source_view: 'lender_loan_performance_v', required_integration: 'los', directionality: 'higher_is_better', db_metric_key: null, ui_component: 'LoanPerformance.tsx' },
  { metric_key: 'loan_performance.applications',      section: 'loan_performance', display_name: 'Applications (Monthly)',   status: 'not_connected', source_view: 'lender_loan_performance_v', required_integration: 'los', directionality: 'higher_is_better', db_metric_key: null, ui_component: 'LoanPerformance.tsx' },
  { metric_key: 'loan_performance.avg_decision_time', section: 'loan_performance', display_name: 'Avg Decision Time',       status: 'not_connected', source_view: 'lender_loan_performance_v', required_integration: 'los', directionality: 'lower_is_better',  db_metric_key: null, ui_component: 'LoanPerformance.tsx' },
  { metric_key: 'loan_performance.default_rate',      section: 'loan_performance', display_name: 'Default Rate',             status: 'not_connected', source_view: 'lender_loan_performance_v', required_integration: 'los', directionality: 'lower_is_better',  db_metric_key: null, ui_component: 'LoanPerformance.tsx' },
  { metric_key: 'loan_performance.detail_table',      section: 'loan_performance', display_name: 'Detail by Loan Type',     status: 'not_connected', source_view: 'lender_loan_performance_v', required_integration: 'los', directionality: 'neutral',          db_metric_key: null, ui_component: 'LoanPerformance.tsx' },
  { metric_key: 'loan_performance.volume_chart',      section: 'loan_performance', display_name: 'Volume & Conversion',     status: 'not_connected', source_view: 'lender_loan_performance_v', required_integration: 'los', directionality: 'higher_is_better', db_metric_key: null, ui_component: 'LoanVolumeConversionChart.tsx' },
  { metric_key: 'loan_performance.decision_chart',    section: 'loan_performance', display_name: 'Decision Time Trend',     status: 'not_connected', source_view: 'lender_loan_performance_v', required_integration: 'los', directionality: 'lower_is_better',  db_metric_key: null, ui_component: 'ApplicationDecisionTimeChart.tsx' },
  { metric_key: 'loan_performance.type_chart',        section: 'loan_performance', display_name: 'Performance by Type',     status: 'not_connected', source_view: 'lender_loan_performance_v', required_integration: 'los', directionality: 'neutral',          db_metric_key: null, ui_component: 'PerformanceByLoanTypeChart.tsx' },
  { metric_key: 'loan_performance.processing_chart',  section: 'loan_performance', display_name: 'Processing Time by Stage',status: 'not_connected', source_view: 'lender_loan_performance_v', required_integration: 'los', directionality: 'lower_is_better',  db_metric_key: null, ui_component: 'ProcessingTimeByStageChart.tsx' },

  // ── Underserved Market Tab (11 metrics) ───────────────────────────────────
  { metric_key: 'underserved.credit_invisible',     section: 'underserved', display_name: 'Credit Invisible Served',     status: 'not_connected', source_view: null,                         required_integration: 'los',  directionality: 'higher_is_better', db_metric_key: null,            ui_component: 'UnderservedMarket.tsx' },
  { metric_key: 'underserved.underserved_bands',   section: 'underserved', display_name: 'Underserved Score Bands',    status: 'live',          source_view: 'lender_underserved_v',       required_integration: 'none', directionality: 'neutral',          db_metric_key: null,            ui_component: 'UnderservedMarket.tsx' },
  { metric_key: 'underserved.first_time_borrowers', section: 'underserved', display_name: 'First-time Borrowers',        status: 'not_connected', source_view: null,                         required_integration: 'los',  directionality: 'higher_is_better', db_metric_key: null,            ui_component: 'UnderservedMarket.tsx' },
  { metric_key: 'underserved.avg_loan_gap',         section: 'underserved', display_name: 'Avg Loan Gap',                status: 'not_connected', source_view: null,                         required_integration: 'los',  directionality: 'lower_is_better',  db_metric_key: null,            ui_component: 'UnderservedMarket.tsx' },
  { metric_key: 'underserved.capacity_utilization', section: 'underserved', display_name: 'Capacity Utilization',        status: 'not_connected', source_view: null,                         required_integration: 'los',  directionality: 'higher_is_better', db_metric_key: null,            ui_component: 'UnderservedMarket.tsx' },
  { metric_key: 'underserved.population_chart',     section: 'underserved', display_name: 'Population Growth',           status: 'not_connected', source_view: null,                         required_integration: 'los',  directionality: 'higher_is_better', db_metric_key: null,            ui_component: 'UnderservedPopulationGrowthChart.tsx' },
  { metric_key: 'underserved.gap_trend_chart',      section: 'underserved', display_name: 'Avg Loan Gap Trend',          status: 'not_connected', source_view: null,                         required_integration: 'los',  directionality: 'lower_is_better',  db_metric_key: null,            ui_component: 'AverageLoanGapTrendChart.tsx' },
  { metric_key: 'underserved.gap_range_chart',      section: 'underserved', display_name: 'Loan Gap by Amount Range',    status: 'not_connected', source_view: null,                         required_integration: 'los',  directionality: 'neutral',          db_metric_key: null,            ui_component: 'LoanGapByAmountRangeChart.tsx' },
  { metric_key: 'underserved.capacity_chart',       section: 'underserved', display_name: 'Borrower Capacity Util.',     status: 'not_connected', source_view: null,                         required_integration: 'los',  directionality: 'higher_is_better', db_metric_key: null,            ui_component: 'BorrowerCapacityUtilizationChart.tsx' },
  { metric_key: 'underserved.demographics_chart',   section: 'underserved', display_name: 'Demographics Performance',    status: 'not_connected', source_view: null,                         required_integration: 'los',  directionality: 'neutral',          db_metric_key: null,            ui_component: 'UnderservedDemographicsChart.tsx' },
  { metric_key: 'underserved.gap_analysis_table',   section: 'underserved', display_name: 'Detailed Gap Analysis',       status: 'not_connected', source_view: null,                         required_integration: 'los',  directionality: 'neutral',          db_metric_key: null,            ui_component: 'UnderservedMarket.tsx' },

  // ── Financial Health Tab (11 metrics) — MOST LIVE DATA ────────────────────
  { metric_key: 'financial_health.avg_verascore',        section: 'financial_health', display_name: 'Avg VeraScore',             status: 'live',          source_view: 'lender_financial_health_v',  required_integration: 'none',      directionality: 'higher_is_better', db_metric_key: 'avg_verascore',          ui_component: 'FinancialHealth.tsx' },
  { metric_key: 'financial_health.score_consistency',    section: 'financial_health', display_name: 'Score Consistency',         status: 'not_connected', source_view: null,                         required_integration: 'algorithm', directionality: 'higher_is_better', db_metric_key: null,                     ui_component: 'FinancialHealth.tsx' },
  { metric_key: 'financial_health.member_satisfaction',  section: 'financial_health', display_name: 'Member Satisfaction',       status: 'not_connected', source_view: null,                         required_integration: 'survey',    directionality: 'higher_is_better', db_metric_key: null,                     ui_component: 'FinancialHealth.tsx' },
  { metric_key: 'financial_health.stress_reduction',     section: 'financial_health', display_name: 'Stress Reduction',          status: 'not_connected', source_view: null,                         required_integration: 'survey',    directionality: 'higher_is_better', db_metric_key: null,                     ui_component: 'FinancialHealth.tsx' },
  { metric_key: 'financial_health.score_trend_chart',    section: 'financial_health', display_name: 'VeraScore Trends',          status: 'live',          source_view: 'lender_financial_health_v',  required_integration: 'none',      directionality: 'higher_is_better', db_metric_key: 'avg_verascore',          ui_component: 'LaborScoreTrendsChart.tsx' },
  { metric_key: 'financial_health.satisfaction_chart',   section: 'financial_health', display_name: 'Satisfaction Trend',        status: 'not_connected', source_view: null,                         required_integration: 'survey',    directionality: 'higher_is_better', db_metric_key: null,                     ui_component: 'MemberSatisfactionTrendChart.tsx' },
  { metric_key: 'financial_health.score_distribution',   section: 'financial_health', display_name: 'Score Distribution',        status: 'live',          source_view: 'lender_financial_health_v',  required_integration: 'none',      directionality: 'neutral',          db_metric_key: 'score_band_distribution', ui_component: 'ScoreDistributionChart.tsx' },
  { metric_key: 'financial_health.radar_chart',          section: 'financial_health', display_name: 'Financial Health Radar',    status: 'live',          source_view: 'lender_financial_health_v',  required_integration: 'none',      directionality: 'neutral',          db_metric_key: null,                     ui_component: 'FinancialHealthRadarChart.tsx' },
  { metric_key: 'financial_health.stress_chart',         section: 'financial_health', display_name: 'Stress Reduction by Cat.', status: 'not_connected', source_view: null,                         required_integration: 'survey',    directionality: 'higher_is_better', db_metric_key: null,                     ui_component: 'StressReductionByCategoryChart.tsx' },
  { metric_key: 'financial_health.consistency_table',    section: 'financial_health', display_name: 'Consistency Analysis',     status: 'not_connected', source_view: null,                         required_integration: 'algorithm', directionality: 'higher_is_better', db_metric_key: null,                     ui_component: 'FinancialHealth.tsx' },
  { metric_key: 'financial_health.consistency_bars',     section: 'financial_health', display_name: 'Consistency Progress',     status: 'not_connected', source_view: null,                         required_integration: 'algorithm', directionality: 'higher_is_better', db_metric_key: null,                     ui_component: 'FinancialHealth.tsx' },
  // P4C: CoreMath-driven declining score signal
  { metric_key: 'financial_health.declining_score_90d_count', section: 'financial_health', display_name: 'Declining Scores (90d)', status: 'live', source_view: 'vs_member_segment_map', required_integration: 'none', directionality: 'lower_is_better', db_metric_key: 'declining_score_90d', ui_component: 'FinancialHealth.tsx' },
  // P4C-2A: CoreMath-driven high DTI stress signal
  { metric_key: 'financial_health.dti_high_count', section: 'financial_health', display_name: 'High DTI Stress (>40%)', status: 'live', source_view: 'vs_member_segment_map', required_integration: 'none', directionality: 'lower_is_better', db_metric_key: 'dti_high', ui_component: 'FinancialHealth.tsx' },
  // P4C-2B: CoreMath-driven low liquid reserves signal
  { metric_key: 'financial_health.liquidity_months_critical_count', section: 'financial_health', display_name: 'Low Liquid Reserves (<1 mo)', status: 'live', source_view: 'vs_member_segment_map', required_integration: 'none', directionality: 'lower_is_better', db_metric_key: 'liquidity_months_critical', ui_component: 'FinancialHealth.tsx' },
];
